// qs-FilePanel.vue
<template>
  <div class="file-panel">
    <button
      v-for="button in visibleButtons"
      :key="button.id"
      :disabled="isProcessing"
      :class="['file-button', {
        success: successAction === button.id,
        danger: button.id === 'reset'
      }]"
      @click="button.action"
    >
      <component :is="getButtonIcon(button)" />
      <span class="button-text">{{ getButtonText(button) }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { Check, Copy, Download, FileCode, FileImage, FileText, FolderOpen, Info, RotateCcw, Upload } from 'lucide-vue-next'
import type { Component } from 'vue'
import { computed, ref } from 'vue'

interface Props {
  showNewButton?: boolean
  showOpenButton?: boolean
  showSaveButton?: boolean
  showExportButton?: boolean
  showResetButton?: boolean
  showZipExport?: boolean
  showZipImport?: boolean
}

interface ButtonConfig {
  id: string
  icon: Component
  text: string
  action: () => void
  show: boolean
}

type EmitEvent = 'new-file' | 'open-file' | 'duplicate-file' | 'export-file' | 'export-png' | 'export-svg' | 'export-zip' | 'import-zip' | 'reset-cache' | 'show-info'

const props = withDefaults(defineProps<Props>(), {
  showNewButton: true,
  showOpenButton: true,
  showSaveButton: true,
  showExportButton: true,
  showResetButton: true,
  showZipExport: true,
  showZipImport: true
})

const emit = defineEmits<{
  'new-file': []
  'open-file': []
  'duplicate-file': []
  'export-file': []
  'export-png': []
  'export-svg': []
  'export-zip': []
  'import-zip': []
  'reset-cache': []
  'show-info': []
}>()

const isProcessing = ref(false)
const lastAction = ref<string | null>(null)
const successAction = ref<string | null>(null)

const buttonConfigs = computed<ButtonConfig[]>(() => [
  {
    id: 'new',
    icon: FileText,
    text: 'New',
    action: () => handleAction('new', 'new-file'),
    show: props.showNewButton
  },
  {
    id: 'open',
    icon: FolderOpen,
    text: 'Open',
    action: () => handleAction('open', 'open-file'),
    show: props.showOpenButton
  },
  {
    id: 'duplicate',
    icon: Copy,
    text: 'Dupe',
    action: () => handleAction('duplicate', 'duplicate-file'),
    show: props.showOpenButton
  },
  {
    id: 'export-png',
    icon: FileImage,
    text: 'PNG',
    action: () => handleAction('export-png', 'export-png'),
    show: props.showExportButton
  },
  {
    id: 'export-svg',
    icon: FileCode,
    text: 'SVG',
    action: () => handleAction('export-svg', 'export-svg'),
    show: props.showExportButton
  },
  {
    id: 'export-zip',
    icon: Download,
    text: 'Export',
    action: () => handleAction('export-zip', 'export-zip'),
    show: props.showZipExport
  },
  {
    id: 'import-zip',
    icon: Upload,
    text: 'Import',
    action: () => handleAction('import-zip', 'import-zip'),
    show: props.showZipImport
  },
  {
    id: 'info',
    icon: Info,
    text: 'About',
    action: () => emit('show-info'),
    show: true
  },
  {
    id: 'reset',
    icon: RotateCcw,
    text: 'Reset',
    action: () => emit('reset-cache'),
    show: props.showResetButton
  }
])

const visibleButtons = computed(() => buttonConfigs.value.filter(button => button.show))

const handleAction = (actionId: string, emitEvent: EmitEvent) => {
  if (isProcessing.value) return

  isProcessing.value = true
  lastAction.value = actionId

  switch (emitEvent) {
    case 'new-file':
      emit('new-file')
      break
    case 'open-file':
      emit('open-file')
      break
    case 'duplicate-file':
      emit('duplicate-file')
      break
    case 'export-file':
      emit('export-file')
      break
    case 'export-png':
      emit('export-png')
      break
    case 'export-svg':
      emit('export-svg')
      break
    case 'export-zip':
      emit('export-zip')
      break
    case 'import-zip':
      emit('import-zip')
      break
    case 'reset-cache':
      emit('reset-cache')
      break
    case 'show-info':
      emit('show-info')
      break
  }

  successAction.value = actionId
  setTimeout(() => {
    successAction.value = null
  }, 1500)

  isProcessing.value = false
}

const getButtonText = (button: ButtonConfig): string => {
  if (isProcessing.value && lastAction.value === button.id) {
    return '...'
  }
  return button.text
}

const getButtonIcon = (button: ButtonConfig): Component => {
  if (successAction.value === button.id) {
    return Check
  }
  return button.icon
}
</script>

<style scoped>
.file-panel {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: fit-content;
  min-width: 44px;
  border-radius: theme('borderRadius.DEFAULT');
  overflow: hidden;
}

.file-button {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
  gap: 8px !important;
  width: auto !important;
  min-width: 44px !important;
  height: 44px !important;
  padding: 0 15px !important;
  margin: 0;
  border: 0;
  white-space: nowrap;
  border-radius: 0 !important;
  background-color: theme('colors.creme.lighter');
  color: theme('colors.text.DEFAULT');
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  outline: none;
}

.file-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.button-text {
  display: none;
  font-size: 12px;
  color: theme('colors.text.DEFAULT');
  font-weight: 500;
}

.file-panel:hover .button-text {
  display: block;
}

.file-button svg {
  flex-shrink: 0;
  width: 14px !important;
  height: 14px !important;
  stroke-width: 1.5;
  stroke: currentColor;
  fill: none;
}

.file-button:hover {
  background-color: theme('colors.creme.hoverbutton') !important;
}

.file-button.success {
  background-color: theme('colors.success.light') !important;
  color: theme('colors.success.dark') !important;
}

.file-button.success .button-text {
  color: theme('colors.success.dark') !important;
  font-weight: 600;
}

.file-button.danger {
  background-color: theme('colors.danger.light') !important;
  color: theme('colors.danger.DEFAULT') !important;
}

.file-button.danger .button-text {
  color: theme('colors.danger.DEFAULT') !important;
  font-weight: 600;
}

.file-button.danger:hover {
  background-color: theme('colors.danger.light') !important;
}

.file-button.danger svg {
  color: theme('colors.danger.DEFAULT') !important;
}
</style>